/**
 * Redaction for error-correlated network request/response context.
 *
 * The SDK's main `redact.ts` only handles rrweb DOM-field masking, so it can't
 * scrub request/response payloads. This module is a small, pure, dependency-free
 * helper that redacts well-known sensitive keys/headers and truncates bodies so
 * the opt-in `requestContext` on an error report never leaks secrets or bloats
 * the payload. Covered by __tests__/redact-network.test.ts.
 */

const REDACTED = '[REDACTED]';
const DEFAULT_MAX_BODY_LEN = 2048;

// Substring match (case-insensitive) against object keys / header names.
const SENSITIVE_KEY_PATTERNS = [
    'password',
    'passwd',
    'secret',
    'token',
    'apikey',
    'api_key',
    'authorization',
    'auth',
    'cookie',
    'session',
    'creditcard',
    'credit_card',
    'cardnumber',
    'card_number',
    'cvv',
    'cvc',
    'ssn',
];

function isSensitiveKey(key: string): boolean {
    const k = key.toLowerCase();
    return SENSITIVE_KEY_PATTERNS.some((p) => k.includes(p));
}

function redactJsonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(redactJsonValue);
    }
    if (value && typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            out[k] = isSensitiveKey(k) ? REDACTED : redactJsonValue(v);
        }
        return out;
    }
    return value;
}

function truncate(text: string, maxLen: number): string {
    if (text.length <= maxLen) {
        return text;
    }
    return `${text.slice(0, maxLen)}...[truncated ${text.length - maxLen} chars]`;
}

/**
 * Redact a request/response body. JSON bodies get their sensitive keys replaced
 * with [REDACTED]; non-JSON bodies are passed through (truncated). Always
 * truncated to `maxLen`.
 */
export function redactBodyString(body: string, maxLen: number = DEFAULT_MAX_BODY_LEN): string {
    if (!body) {
        return body;
    }
    try {
        const parsed = JSON.parse(body);
        return truncate(JSON.stringify(redactJsonValue(parsed)), maxLen);
    } catch {
        return truncate(body, maxLen);
    }
}

/** Redact sensitive header values (Authorization, Cookie, etc.). */
export function redactHeaders(headers: Record<string, string>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(headers)) {
        out[k] = isSensitiveKey(k) ? REDACTED : v;
    }
    return out;
}
